import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Chart, registerables } from 'chart.js';
import 'chart.js/auto';
import { FaChartLine, FaArrowLeft } from 'react-icons/fa';

Chart.register(...registerables);

const SIPGrowthChart = () => {
  const { monthlyInvestment, investmentPeriod, expectedReturn } = useParams();
  const [chartData, setChartData] = useState({});
  const chartRef = React.useRef(null);
  const chartInstance = React.useRef(null);

  useEffect(() => {
    if (!monthlyInvestment || !investmentPeriod || !expectedReturn) return;

    const monthlyInvestmentNum = parseFloat(monthlyInvestment);
    const investmentPeriodNum = parseFloat(investmentPeriod);
    const expectedReturnNum = parseFloat(expectedReturn) / 100 / 12;

    // Generate data for chart
    const years = Array.from({ length: investmentPeriodNum }, (_, i) => i + 1);
    const values = years.map(year => {
      const months = year * 12;
      return monthlyInvestmentNum * 
        ((Math.pow(1 + expectedReturnNum, months) - 1) / expectedReturnNum) * 
        (1 + expectedReturnNum);
    });

    setChartData({
      labels: years.map(y => `Year ${y}`),
      datasets: [{
        label: 'Investment Growth',
        data: values,
        fill: true,
        borderColor: '#6366f1',
        tension: 0.1,
        backgroundColor: 'rgba(99, 102, 241, 0.1)'
      }]
    });
  }, [monthlyInvestment, investmentPeriod, expectedReturn]);

  useEffect(() => {
    if (chartData.labels && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      chartInstance.current = new Chart(chartRef.current, {
        type: 'line',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            y: {
              beginAtZero: true,
              ticks: {
                callback: function(value) {
                  return '₹' + value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                }
              }
            }
          },
          plugins: {
            legend: {
              position: 'top'
            },
            title: {
              display: true,
              text: 'SIP Growth Over Time'
            }
          }
        }
      });
    }
  }, [chartData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center mb-8">
          <Link 
            to="/indian-finance/sip"
            className="inline-flex items-center text-purple-600 hover:text-purple-800"
          >
            <FaArrowLeft className="mr-2" />
            Back to SIP Calculator
          </Link>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">SIP Growth Chart</h1>
          
          <div className="mb-8">
            <canvas ref={chartRef} className="w-full h-96" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900">Monthly Investment</h3>
              <p className="mt-2 text-2xl font-bold text-purple-600">
                ₹{monthlyInvestment}
              </p>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900">Investment Period</h3>
              <p className="mt-2 text-2xl font-bold text-purple-600">
                {investmentPeriod} Years
              </p>
            </div>

            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900">Expected Return</h3>
              <p className="mt-2 text-2xl font-bold text-purple-600">
                {expectedReturn}%
              </p>
            </div>
          </div>

          <div className="mt-8">
            <p className="text-gray-600">
              This chart shows the growth of your SIP investment over time. The blue line represents your investment value at the end of each year.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SIPGrowthChart;
